/**
 * EditRequestModal Component
 * Modal for editing an existing transfer bond request
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect, useMemo } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  editTransferBondRequest,
  getStates,
  getCountiesByState,
  getActiveAgentsByCounty,
} from '../../../services/oathTransferBondService';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  OathModal,
  OathButton,
  OathInput,
  OathSelect,
  OathAlert,
  OathSpinner,
} from '../../shared/OathUI';
import { REQUEST_FIELD_MAX_LENGTH } from './shared/requestFieldLimits';

const EditRequestModal = ({ isOpen, onClose, onSuccess, bondDetails }) => {
  const { user } = useOathAuth();

  // Form state - initialized from bondDetails
  const [agencyId, setAgencyId] = useState('');
  const [stateCd, setStateCd] = useState('');
  const [countyId, setCountyId] = useState('');
  const [jail, setJail] = useState('');
  const [court, setCourt] = useState('');
  const [numBonds, setNumBonds] = useState('1');
  const [totalAmount, setTotalAmount] = useState('');
  const [defendantFirstName, setDefendantFirstName] = useState('');
  const [defendantLastName, setDefendantLastName] = useState('');

  // Data states
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [activeAgentsInfo, setActiveAgentsInfo] = useState(null);

  // Loading states
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success state
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Get requesting agencies from user
  const requestingAgencies = useMemo(() => {
    return (
      user?.agencies
        ?.filter((agency) => agency.canRequest)
        .map((agency) => ({
          value: agency.agencyId.toString(),
          label: `${agency.dbaAgencyName} (${agency.agencyId})`,
        })) || []
    );
  }, [user]);

  // Initialize form from bondDetails
  useEffect(() => {
    if (bondDetails && isOpen) {
      setAgencyId(bondDetails.requestingAgentID?.toString() || '');
      setStateCd(bondDetails.state || '');
      setJail(bondDetails.jailLocation || '');
      setCourt(bondDetails.courtPlace || '');
      setNumBonds(bondDetails.numberOfBonds?.toString() || '1');
      setTotalAmount(bondDetails.totalBondAmount?.toString() || '');
      setDefendantFirstName(bondDetails.defendantFirstName || '');
      setDefendantLastName(bondDetails.defendantLastName || '');
    }
  }, [bondDetails, isOpen]);

  // Fetch states on mount
  useEffect(() => {
    if (!isOpen) return;

    const fetchStates = async () => {
      setLoadingStates(true);
      const response = await getStates();
      if (response.success) {
        setStates(
          response.data.map((s) => ({
            value: s.stateCd,
            label: s.stateName.toUpperCase(),
          }))
        );
      }
      setLoadingStates(false);
    };

    fetchStates();
  }, [isOpen]);

  // Fetch counties when state changes
  useEffect(() => {
    if (!stateCd) {
      setCounties([]);
      return;
    }

    const fetchCounties = async () => {
      setLoadingCounties(true);

      const response = await getCountiesByState(stateCd);
      if (response.success) {
        setCounties(
          response.data.map((c) => ({
            value: c.countyId.toString(),
            label: c.county,
          }))
        );
        // Set county from bondDetails if state matches
        if (bondDetails?.state === stateCd && bondDetails?.countyId) {
          setCountyId(bondDetails.countyId.toString());
        }
      }
      setLoadingCounties(false);
    };

    fetchCounties();
  }, [stateCd, bondDetails]);

  // Fetch active agents when county changes
  useEffect(() => {
    if (!countyId) {
      setActiveAgentsInfo(null);
      return;
    }

    const fetchAgents = async () => {
      setLoadingAgents(true);
      const response = await getActiveAgentsByCounty(countyId);
      if (response.success) {
        setActiveAgentsInfo(response.data);
      }
      setLoadingAgents(false);
    };

    fetchAgents();
  }, [countyId]);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!agencyId) newErrors.agencyId = 'Agency is required';
    if (!stateCd) newErrors.stateCd = 'State is required';
    if (!countyId) newErrors.countyId = 'County is required';
    if (!jail.trim()) newErrors.jail = 'Jail location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    const requestData = {
      AgencyId: parseInt(agencyId),
      State: stateCd.toUpperCase(),
      CountyId: parseInt(countyId),
      Jail: jail.trim(),
      Court: court.trim(),
      NumBonds: parseInt(numBonds) || 1,
      TotalAmount: parseFloat(totalAmount) || 0,
      DefendantFirstName: defendantFirstName.trim().toUpperCase(),
      DefendantLastName: defendantLastName.trim().toUpperCase(),
    };

    const response = await editTransferBondRequest(bondDetails.transferBondTransactionID, requestData);

    if (response.success) {
      toast.success('Request updated successfully.');
      setIsSubmitted(true);
      onSuccess?.();
    } else {
      toast.error(response.error || 'Failed to update request.');
      setErrors({ submit: response.error || 'Failed to update request' });
    }

    setIsSubmitting(false);
  };

  // Handle close
  const handleClose = () => {
    setErrors({});
    setIsSubmitted(false);
    setActiveAgentsInfo(null);
    onClose();
  };

  const selectedCounty = counties.find((c) => c.value === countyId);
  const isTexas = stateCd === 'TX';

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isSubmitted ? 'Request Updated' : 'Edit Transfer Bond Request'}
      size="md"
      footer={!isSubmitted ? (
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            loading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </OathButton>
        </div>
      ) : null}
    >
      {isSubmitted ? (
        // Success state
        <div className="text-center py-4">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Request Updated!</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Your changes have been saved. Active posting agents will be notified of the updates.
            Any existing quotes will be declined.
          </p>
          <OathButton onClick={handleClose} className="w-full sm:w-auto">
            Close
          </OathButton>
        </div>
      ) : (
        // Form
        <div className="space-y-5">
          {/* Warning */}
          <OathAlert variant="warning">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Editing this request will decline all current quotes. Active agents will be notified of the changes.
              </p>
            </div>
          </OathAlert>

          {/* Submit error */}
          {errors.submit && (
            <OathAlert variant="error" message={errors.submit} />
          )}

          {/* Agency */}
          <OathSelect
            label="My Agency"
            value={agencyId}
            onChange={setAgencyId}
            options={requestingAgencies}
            placeholder="Select agency..."
            required
            error={errors.agencyId}
          />

          {/* State */}
          <OathSelect
            label="State"
            value={stateCd}
            onChange={(val) => {
              setStateCd(val);
              setCountyId('');
            }}
            options={states}
            placeholder="Select state..."
            loading={loadingStates}
            required
            error={errors.stateCd}
          />

          {/* County */}
          <OathSelect
            label="County"
            value={countyId}
            onChange={setCountyId}
            options={counties}
            placeholder="Select county..."
            disabled={!stateCd}
            loading={loadingCounties}
            required
            error={errors.countyId}
          />

          {/* Active agents info */}
          {countyId && !loadingAgents && activeAgentsInfo && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm">
              {activeAgentsInfo.numberOfAgencies > 0 ? (
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>{selectedCounty?.label}</strong> county has{' '}
                  <strong>{activeAgentsInfo.numberOfAgencies}</strong> agencies who can post.{' '}
                  <strong>{activeAgentsInfo.numberOfContacts}</strong> will be notified of changes.
                </p>
              ) : (
                <p className="text-gray-700 dark:text-gray-300">
                  Currently, no agencies are available to post in <strong>{selectedCounty?.label}</strong> County.
                </p>
              )}
            </div>
          )}

          {loadingAgents && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <OathSpinner size="sm" /> Loading active agents...
            </div>
          )}

          {/* Jail */}
          <OathInput
            label="Jail"
            value={jail}
            onChange={setJail}
            placeholder="Jail location"
            maxLength={REQUEST_FIELD_MAX_LENGTH.jail}
            required
            error={errors.jail}
          />

          {/* Court */}
          <OathInput
            label="Courthouse"
            value={court}
            onChange={setCourt}
            placeholder="Courthouse (optional)"
            maxLength={REQUEST_FIELD_MAX_LENGTH.court}
          />

          {/* Number of Bonds */}
          <OathInput
            label="Number of Bonds"
            type="number"
            value={numBonds}
            onChange={setNumBonds}
            placeholder="1"
            helperText={isTexas ? 'For accurate quotes in Texas, please provide the number of bonds.' : undefined}
          />

          {/* Total Bond Amount */}
          <OathInput
            label="Total Bond Amount"
            type="number"
            value={totalAmount}
            onChange={setTotalAmount}
            placeholder="0.00"
            prefix="$"
          />

          {/* Defendant Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <OathInput
              label="Defendant First Name"
              value={defendantFirstName}
              onChange={(val) => setDefendantFirstName(val.toUpperCase())}
              placeholder="FIRST NAME"
              maxLength={REQUEST_FIELD_MAX_LENGTH.defendantFirstName}
            />
            <OathInput
              label="Defendant Last Name"
              value={defendantLastName}
              onChange={(val) => setDefendantLastName(val.toUpperCase())}
              placeholder="LAST NAME"
              maxLength={REQUEST_FIELD_MAX_LENGTH.defendantLastName}
            />
          </div>
        </div>
      )}
    </OathModal>
  );
};

export default EditRequestModal;
